import { useRef } from 'react';

import { Box, List, useMediaQuery, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import { Link } from 'react-router-dom';

import { ScrollShade, SkopeiConnectLogo } from '~components';
import { pagePathSegments } from '~constants';
import { useScrollShade, useSession } from '~hooks';
import useAuthorize from '~hooks/useAuthorize';
import { Dimens } from '~themes';

import SideMenuItem from './items/sideMenuItem';
import SideMenuSkeleton from './items/sideMenuSkeleton';
import { entryKey, pruneEntries } from './menuVisibility';
import { SideMenuEntry } from './types';

interface Props {
	/** Primary entries, rendered from the top. */
	entries: SideMenuEntry[];
	/** Entries pinned to the bottom (e.g. Wiki / Settings). */
	bottomEntries: SideMenuEntry[];
}

/**
 * The side menu's chrome: logo, the scrollable navigation area with its scroll
 * shadows, and the bottom-pinned group. It role-prunes the entries it is given
 * and maps each to a {@link SideMenuItem}; it holds no menu data of its own.
 */
const SideMenuContainer = ({ entries, bottomEntries }: Props) => {
	const theme = useTheme();
	const showLabels = useMediaQuery(theme.breakpoints.up('md'));
	const { authorize } = useAuthorize();
	const { user } = useSession();

	const navRef = useRef<HTMLDivElement>(null);
	const shade = useScrollShade(navRef);

	// Only prune once the user (and thus their roles) is loaded; otherwise the
	// menu would render a partial list and jump when roles arrive. The last good
	// result is cached, so a transient user loss during a token refresh keeps the
	// menu in place instead of flashing back to the skeleton.
	const isVisible = (roles?: SideMenuEntry['roles']) => !roles || authorize(roles);
	const visibleRef = useRef<{ top: SideMenuEntry[]; bottom: SideMenuEntry[] } | null>(null);
	if (user) {
		visibleRef.current = {
			top: pruneEntries(entries, isVisible),
			bottom: pruneEntries(bottomEntries, isVisible),
		};
	}
	const visible = visibleRef.current;

	return (
		<Stack
			direction='column'
			sx={{
				width: { xs: Dimens.fixed(68), md: Dimens.fixed(248) },
				height: '100%',
				minHeight: 0,
				overflow: 'hidden',
				paddingTop: Dimens.spacingXl,
				paddingX: { xs: Dimens.spacingNone, md: Dimens.spacingMd },
				alignItems: { xs: 'center', md: 'stretch' },
				gap: { xs: Dimens.spacingXl, md: Dimens.spacingLg },
			}}
		>
			<Box
				component={Link}
				to={pagePathSegments.Home}
				sx={{
					display: 'flex',
					flexShrink: Dimens.spacingNone,
					textDecoration: 'none',
					color: 'inherit',
				}}
			>
				<SkopeiConnectLogo showLabel={showLabels} />
			</Box>
			<Box
				component='nav'
				ref={navRef}
				sx={{
					width: showLabels ? '100%' : 'auto',
					flex: 1,
					minHeight: 0,
					display: 'flex',
					flexDirection: 'column',
					position: 'relative',
					overflowY: 'auto',
					overflowX: 'hidden',
					// Hide the scrollbar but keep it scrollable.
					'&::-webkit-scrollbar': { display: 'none' },
					msOverflowStyle: 'none',
					scrollbarWidth: 'none',
					gap: Dimens.spacingXl,
				}}
			>
				<ScrollShade edge='top' visible={shade.top} />
				{visible ?
					<>
						<List
							disablePadding
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: { xs: Dimens.spacingXs, md: Dimens.spacingNone, xl: Dimens.spacingXs },
								paddingTop: Dimens.spacingLg,
							}}
						>
							{visible.top.map((entry) => (
								<SideMenuItem key={entryKey(entry)} item={entry} showLabel={showLabels} />
							))}
						</List>
						{/* Pinned to the bottom while everything fits; when the menu overflows
						    the auto margin collapses and these scroll with the rest. */}
						<List
							disablePadding
							sx={{
								mt: 'auto',
								display: 'flex',
								flexDirection: 'column',
								gap: { xs: Dimens.spacingXs, md: Dimens.spacingNone, xl: Dimens.spacingXs },
							}}
						>
							{visible.bottom.map((entry) => (
								<SideMenuItem key={entryKey(entry)} item={entry} showLabel={showLabels} />
							))}
						</List>
					</>
				:	<SideMenuSkeleton showLabel={showLabels} />}
				<ScrollShade edge='bottom' visible={shade.bottom} />
			</Box>
		</Stack>
	);
};

export default SideMenuContainer;
